import cn from 'classnames';
import omit from 'lodash/omit';
import { useRouter } from 'next/router';
import type { FC, SyntheticEvent } from 'react';
import { memo } from 'react';

import { Badge, Icon, Typography } from '@sravni/react-design-system';
import { Star, Tooltip } from '@sravni/react-icons';
import { useBoolean } from '@sravni/react-utils';

import type { IClientMicrocreditListItem } from '@src/@types/clienttypes';
import { Currency } from '@src/@types/currency';
import type { AffSub4 } from '@src/@types/microcredits';
import { AdvertisingButton } from '@src/components/AdvertisingButton';
import { CurtainPopup } from '@src/components/CurtainPopup';
import { FreeButton } from '@src/components/FreeButton';
import { Logo } from '@src/components/Logotype';
import { Pixel } from '@src/components/Pixel';
import { AdditionalService } from '@src/components/ProductList/components/CardV2/components/AdditionalService';
import { sendAdditionalConditionsClickEvent } from '@src/helpers/analyticsEvents';
import { getAmountString, humanizeTermRange } from '@src/helpers/common';
import { isSpecialPageWithoutQuery } from '@src/utils/routing';

import { useCardClick } from '../Card/useCardClick';

import { ApprovalProbability } from './components/ApprovalProbability';
import { ContentItem } from './components/ContentItem';
import styles from './styles.module.scss';

interface ICardDesktopProps {
    className?: string;
    offer: IClientMicrocreditListItem;
    position: number;
    withDisclaimer?: boolean;
}

const CardV2: FC<ICardDesktopProps> = memo(
    /* eslint-disable complexity */
    // eslint-disable-next-line max-statements
    ({ className, offer, position, withDisclaimer = true }) => {
        const { name, organization, advertising, approvalProbability, _id } = offer;
        const { rating } = organization;
        const { isPartner, labelText } = advertising || {};
        const [label] = labelText?.split(';') || [];
        const router = useRouter();
        const { asPath } = router;
        const isSpecial = isSpecialPageWithoutQuery(asPath);

        const badges = [];

        const [isAdditionalConditionsOpen, setAdditionalConditionsOpen] = useBoolean(false);

        const onTooltipClick = (event: SyntheticEvent) => {
            event.stopPropagation();
            setAdditionalConditionsOpen.on();

            const eventLabel = `${offer.organization.name}|${offer.name}`;
            sendAdditionalConditionsClickEvent(eventLabel);
        };

        if (isPartner) {
            const text = 'Mы рекомендуем';
            badges.push(<Badge key={text} color="orange" variant="primary" text={text} />);
        } else if (label) {
            badges.push(<Badge key={label} color="blue" variant="primary" text={label} />);
        }

        const amount = getAmountString(offer.details.amount.to || offer.details.amount.from, Currency.RUB);
        const formattedTerm = humanizeTermRange(omit(offer.details.term, 'from'), {
            useAlternativeFormat: true,
            initialForm: false,
        });

        const { sourceLink, pixelDisplayLink, logoLink, productLink, freeButtonLink } = useCardClick({
            offer,
            position,
        });

        const affSub4: AffSub4 = {
            organizationName: organization.name,
            productName: name,
            organizationId: organization._id,
            productId: _id,
        };

        const hasAdditionalConditions = offer.additionalServices?.exist;

        return (
            <div className={cn(styles.cardWrapper, className)}>
                <div className={cn(styles.card)} data-qa="Card">
                    <div className={styles.wrapper}>
                        <div className={cn(styles.header)}>
                            <div className={styles.text}>
                                <div className={styles.text}>{organization.name}</div>
                                {rating && (
                                    <div className={styles.ratingBlock}>
                                        <Icon className={styles.star} size={16} color="orange" icon={<Star />} />
                                        <span>{rating.toFixed(2)}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {!!badges.length && !isSpecial && <div>{badges}</div>}
                        <div className={styles.card_content}>
                            {pixelDisplayLink && (
                                <Pixel
                                    source={`${sourceLink}|position_${position}`}
                                    link={pixelDisplayLink}
                                    affSub4={affSub4}
                                />
                            )}
                            <div className={styles.content_row}>
                                <ContentItem title="Cумма">{amount}</ContentItem>
                                <ContentItem title="На срок">{formattedTerm}</ContentItem>
                                <ContentItem title="Вероятность одобрения">
                                    <ApprovalProbability category={approvalProbability?.category} />
                                </ContentItem>
                                <ContentItem title="Доп. условия">
                                    {hasAdditionalConditions ? 'Есть' : 'Нет'}
                                    <Icon
                                        className={styles.valueIcon}
                                        size={20}
                                        icon={<Tooltip onClick={onTooltipClick} />}
                                    />
                                </ContentItem>
                            </div>
                        </div>
                    </div>
                    <Logo
                        className={styles.header_logo}
                        link={logoLink}
                        analytics={offer}
                        logo={organization.logotypes?.android}
                        isPartner={offer.isBankPartner || offer.advertising?.isPartner}
                        size={44}
                    />
                    <div className={styles.actions}>
                        {advertising ? (
                            <>
                                <FreeButton className={styles.button} analytics={offer} link={freeButtonLink} />
                                <AdvertisingButton
                                    className={styles.button}
                                    source={`${sourceLink}|position${position}`}
                                    advSub={advertising.advSub}
                                    analytics={offer}
                                    productLinkHref={productLink?.href}
                                    affSub4={affSub4}
                                />
                            </>
                        ) : (
                            <FreeButton className={styles.button} analytics={offer} link={freeButtonLink} />
                        )}
                    </div>
                    {withDisclaimer && offer.advertising && (
                        <Typography.Text className={styles.disclaimer}>
                            Реклама {offer.organization.fullName || offer.organization.name}
                        </Typography.Text>
                    )}
                    <CurtainPopup
                        visible={isAdditionalConditionsOpen}
                        onClose={setAdditionalConditionsOpen.off}
                        title="Дополнительные условия"
                    >
                        <AdditionalService hasAdditionalConditions={hasAdditionalConditions} />
                    </CurtainPopup>
                </div>
            </div>
        );
    },
);
/* eslint-enable complexity */

export default CardV2;
